/** @jsxImportSource @emotion/react */

import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as CorrectAnswerIcon } from 'assets/icons/correct-answer.svg'
import { findOneExtensionExamSection_findOneExtensionExamSection_extensionQuestion as Question } from '__generatedTypes__/findOneExtensionExamSection'
import { getAnswerLetterLabel } from 'utils/getAnswerLetterLabel'
import * as classes from './styles'

export type QuestionPreviewProps = {
  questionIndex: number
  question: Question
  expanded: boolean
  onExpandClick: () => void
  onEditClick: () => void
  isQuestionOnly?: boolean
}

export const QuestionPreview = ({
  questionIndex,
  question,
  expanded,
  onExpandClick,
  onEditClick,
  isQuestionOnly,
}: QuestionPreviewProps) => (
  <Accordion
    expanded={expanded}
    onChange={onExpandClick}
    css={[
      classes.accordion,
      !isQuestionOnly && !question.mainSolution && classes.accordionEmpty,
      isQuestionOnly && classes.accordionSingle,
    ]}
  >
    <AccordionSummary css={classes.accordionSummary}>
      <div css={[classes.accordionSummaryBlock, classes.accordionSummaryTitleBlock]}>
        <Typography variant="body3" css={classes.index} data-testid="question-index">
          {questionIndex}
        </Typography>
        <Typography variant="body2" css={classes.title} data-testid="question-title">
          Question #{questionIndex} (#{question.order + 1} in exam)
        </Typography>
      </div>
      <div css={classes.accordionSummaryBlock}>
        {!isQuestionOnly && (
          <EditIcon
            css={[classes.icon, classes.iconEdit]}
            onClick={(e) => {
              e.stopPropagation()
              onEditClick()
            }}
            data-testid="question-edit-button"
          />
        )}
        <ExpandMoreRoundedIcon
          css={[classes.iconChevron, expanded && classes.iconChevronOpen]}
          data-testid="question-expand-icon"
        />
      </div>
    </AccordionSummary>
    <AccordionDetails css={classes.accordionDetails}>
      <div
        css={classes.htmlBlock}
        dangerouslySetInnerHTML={{ __html: question.question }}
        data-testid="question-text"
      />
      {!isQuestionOnly && (
        <>
          <div
            css={[classes.htmlBlock, classes.solutionBlock, classes.mainSolutionBlock]}
            dangerouslySetInnerHTML={{ __html: question.mainSolution }}
            data-testid="main-solution-text"
          />
          <div css={classes.mainSolutionVideoBlock}>
            Main Solution Video:{' '}
            <span data-testid="main-solution-video-link">
              {question.mainVideoSolution ? (
                <a href={question.mainVideoSolution} target="_blank" rel="noopener noreferrer">
                  {question.mainVideoSolution}
                </a>
              ) : (
                '-'
              )}
            </span>
          </div>
        </>
      )}
      {question.answer.map((answer, index) => {
        const answerLabel = getAnswerLetterLabel(index)
        return (
          <div key={index} data-testid={`question-answer-${index}`}>
            <div css={classes.answer}>
              <Typography variant="body3" css={classes.answerLabel}>
                {answerLabel}.
                {answerLabel === question.correctOption && <CorrectAnswerIcon css={classes.correctAnswerIcon} />}
              </Typography>
              <div
                css={classes.htmlBlock}
                dangerouslySetInnerHTML={{ __html: answer }}
                data-testid={`answer-${index}-text`}
              />
            </div>
            {!isQuestionOnly && (
              <div
                css={[classes.htmlBlock, classes.solutionBlock]}
                dangerouslySetInnerHTML={{ __html: question.answerSolution?.[index] ?? '' }}
                data-testid={`answer-${index}-solution-text`}
              />
            )}
          </div>
        )
      })}
    </AccordionDetails>
  </Accordion>
)
